import React, { useEffect, useState, useCallback, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { getRiskCates } from '../../api/riskCateApi';
import { getManagers } from '../../api/managerApi';
import RiskCateView from './RiskCateView';
import RiskCateEdit from './RiskCateEdit';
import RiskCateForm from './RiskCateForm';
import {
  Box, Typography, Button, Paper, Stack, IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Category as CategoryIcon,
  AccountTree as AccountTreeIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';

// 플랫 배열 → 중첩 트리 변환
function buildTree(flatList) {
  const map = {};
  const roots = [];
  flatList.forEach(item => { map[item.id] = { ...item, children: [] }; });
  flatList.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(map[item.id]);
    } else {
      roots.push(map[item.id]);
    }
  });
  return roots;
}

// 폴더 노드의 id 전체 수집 (초기 자동 펼침용)
function collectFolderIds(nodes) {
  const ids = new Set();
  function traverse(list) {
    list.forEach(node => {
      if (node.category_type === 'folder') ids.add(node.id);
      if (node.children.length > 0) traverse(node.children);
    });
  }
  traverse(nodes);
  return ids;
}

function RiskCateList() {
  const { token } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [tree, setTree] = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('view'); // view | edit | new
  const [companies, setCompanies] = useState([]);
  const [selectedComCode, setSelectedComCode] = useState('');

  const loadList = useCallback(() => {
    if (!token || !selectedComCode) {
      setItems([]);
      setTree([]);
      setExpandedIds(new Set());
      return;
    }
    getRiskCates(token, selectedComCode)
      .then(data => {
        const flat = Array.isArray(data) ? data : [];
        setItems(flat);
        const built = buildTree(flat);
        setTree(built);
        setExpandedIds(collectFolderIds(built));
      })
      .catch(err => console.error('분류 목록 조회 실패:', err));
  }, [token, selectedComCode]);

  useEffect(() => {
    getManagers(token)
      .then(data => setCompanies(data))
      .catch(err => console.error('회사 목록 조회 실패:', err));
  }, [token]);

  useEffect(() => { loadList(); }, [loadList]);

  const handleComCodeChange = (e) => {
    setSelectedComCode(e.target.value);
    setSelectedId(null);
    setMode('view');
  };

  const handleSelect = (id) => {
    setSelectedId(id);
    setMode('view');
  };

  const toggleExpand = (e, id) => {
    e.stopPropagation();
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const renderTree = (nodes, depth = 0) =>
    nodes.map((node, idx) => {
      const isSelected = selectedId === node.id;
      const isFolder = node.category_type === 'folder';
      const hasChildren = node.children.length > 0;
      const isExpanded = expandedIds.has(node.id);
      const isLast = idx === nodes.length - 1;

      return (
        <React.Fragment key={node.id}>
          <Box
            onClick={() => handleSelect(node.id)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              pl: depth * 2.5 + 0.5,
              pr: 1,
              py: 0.8,
              cursor: 'pointer',
              bgcolor: isSelected ? '#e3f2fd' : 'transparent',
              '&:hover': { bgcolor: isSelected ? '#e3f2fd' : '#f8f9fa' },
              borderLeft: isSelected ? '3px solid #1976d2' : '3px solid transparent',
              transition: 'all 0.15s',
              borderBottom: !isLast || hasChildren ? '1px solid #f0f0f0' : 'none',
            }}
          >
            {/* 펼침/접힘 토글 */}
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(e) => toggleExpand(e, node.id)}
                sx={{ p: 0.2, color: '#6c757d' }}
              >
                {isExpanded
                  ? <ExpandMoreIcon sx={{ fontSize: '1rem' }} />
                  : <ChevronRightIcon sx={{ fontSize: '1rem' }} />}
              </IconButton>
            ) : (
              <Box sx={{ width: 22, flexShrink: 0 }} />
            )}

            {/* 타입 아이콘 */}
            {isFolder ? (
              isExpanded
                ? <FolderOpenIcon sx={{ fontSize: '1.1rem', color: '#ffa000', flexShrink: 0 }} />
                : <FolderIcon sx={{ fontSize: '1.1rem', color: '#ffa000', flexShrink: 0 }} />
            ) : (
              <DescriptionIcon sx={{ fontSize: '1.1rem', color: '#42a5f5', flexShrink: 0 }} />
            )}

            {/* 텍스트 */}
            <Box sx={{ flex: 1, minWidth: 0, ml: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: isFolder ? 700 : 500, color: '#212529', lineHeight: 1.3 }}
                noWrap
              >
                {node.risk_category_num}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6c757d', display: 'block' }} noWrap>
                {node.risk_category_context}
              </Typography>
            </Box>

            <Typography variant="caption" sx={{ color: '#adb5bd', flexShrink: 0 }}>
              {node.sort_order}
            </Typography>
          </Box>

          {/* 자식 노드 */}
          {hasChildren && isExpanded && renderTree(node.children, depth + 1)}
        </React.Fragment>
      );
    });

  return (
    <div className="row mt-4">
      {/* 좌측: 트리 목록 */}
      <div className="col-md-5 border-end">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, pl: 0.5 }}>
          <Box sx={{ width: 5, height: 26, bgcolor: '#1976d2', borderRadius: 2 }} />
          <AccountTreeIcon sx={{ color: '#1976d2', fontSize: '1.7rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#2c3e50', flex: 1 }}>
            위험성 분류 관리
          </Typography>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<AddIcon />}
            disabled={!selectedComCode}
            onClick={() => { setSelectedId(null); setMode('new'); }}
            sx={{ height: '32px', fontWeight: 'bold', fontSize: '0.8rem' }}
          >
            새 항목 등록
          </Button>
        </Box>

        <div className="mb-2" style={{ maxWidth: '240px' }}>
          <select
            className="form-select"
            style={{ minWidth: '180px', maxWidth: '240px' }}
            value={selectedComCode}
            onChange={handleComCodeChange}
          >
            <option value="">회사를 선택해주세요.</option>
            {companies.map((c) => (
              <option key={c.M_ComCode} value={c.M_ComCode}>{c.M_ComName}</option>
            ))}
          </select>
        </div>

        <Paper variant="outlined" sx={{ maxHeight: 600, overflowY: 'auto', borderRadius: 1 }}>
          {!selectedComCode ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <CategoryIcon sx={{ fontSize: '2rem', mb: 1, color: '#dee2e6' }} />
              <Typography variant="body2">회사를 선택해주세요.</Typography>
            </Box>
          ) : tree.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <CategoryIcon sx={{ fontSize: '2rem', mb: 1, color: '#dee2e6' }} />
              <Typography variant="body2">등록된 분류가 없습니다.</Typography>
            </Box>
          ) : (
            renderTree(tree)
          )}
        </Paper>
      </div>

      {/* 우측: 상세 / 편집 / 등록 */}
      <div className="col-md-7">
        {mode === 'view' && selectedId && (
          <RiskCateView
            id={selectedId}
            token={token}
            comCode={selectedComCode}
            onEdit={() => setMode('edit')}
            onDeleted={() => { loadList(); setSelectedId(null); setMode('view'); }}
          />
        )}
        {mode === 'edit' && selectedId && (
          <RiskCateEdit
            id={selectedId}
            token={token}
            comCode={selectedComCode}
            allItems={items}
            onCancel={() => setMode('view')}
            onUpdated={() => { loadList(); setMode('view'); }}
          />
        )}
        {mode === 'new' && (
          <RiskCateForm
            token={token}
            comCode={selectedComCode}
            allItems={items}
            onCancel={() => setMode('view')}
            onCreated={() => { loadList(); setMode('view'); }}
          />
        )}
        {!selectedId && mode === 'view' && (
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: '#fcfcfc',
              borderStyle: 'dashed',
              borderRadius: 2,
              mt: 6,
            }}
          >
            <Stack spacing={1} alignItems="center">
              <CategoryIcon sx={{ fontSize: '2.5rem', color: '#dee2e6' }} />
              <Typography color="text.secondary">
                왼쪽 트리에서 분류를 선택하여 상세 정보를 확인하세요.
              </Typography>
            </Stack>
          </Paper>
        )}
      </div>
    </div>
  );
}

export default RiskCateList;
